import React, { useEffect, useRef } from 'react'
import * as d3 from 'd3'
import styles from '../MultivariateAnalysis.module.css';

function Heatmap({ data, xAxisName, yAxisName }) {
    const ref = useRef();

    useEffect(() => {
        const svg = d3.select(ref.current);
        const margin = { top: 20, right: 20, bottom: 60, left: 90 };
        const width = 800 - margin.left - margin.right;
        const height = 350 - margin.top - margin.bottom;

        svg.attr("width", width + margin.left + margin.right)
            .attr("height", height + margin.top + margin.bottom);

        svg.selectAll("*").remove();
        const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

        const xLabels = [...new Set(data.map(d => d.x))];
        const yLabels = [...new Set(data.map(d => d.y))];

        const xScale = d3.scaleBand().range([0, width]).domain(xLabels).padding(0.05);
        const yScale = d3.scaleBand().range([height, 0]).domain(yLabels).padding(0.05);

        // ---------------------------
        // 🔥 FIX: Dynamic color scale
        // ---------------------------
        const maxVal = d3.max(data, d => d.value);
        const colorScale = d3.scaleSequential(d3.interpolateGreens)
            .domain([0, maxVal || 1]);

        const tooltip = d3
            .select("body")
            .append("div")
            .attr("class", styles["d3-tooltip"]);

        const cells = g
            .selectAll(".rect-cell")
            .data(data)
            .join("g")
            .attr("class", "rect-cell-group");

        cells.append("rect")
            .attr("x", d => xScale(d.x))
            .attr("y", d => yScale(d.y))
            .attr("width", xScale.bandwidth())
            .attr("height", yScale.bandwidth())
            .style("fill", d =>
                d.value === 0
                    ? "#ffffff"            // 👉 zero value → white background
                    : colorScale(d.value)  // 👉 non-zero → color scale
            )
            // .style("fill", d => colorScale(d.value))
            .style("stroke", "#dbd1d4ff")
            .style("stroke-width", "1px")
            .on("mouseover", (event, d) => {
                tooltip.transition().duration(200).style("opacity", 0.95);
                tooltip
                    .html(`
                         <div>
                            <div><strong>X:</strong> ${d.x}</div>
                                <div><strong>Y:</strong> ${d.y}</div>
                            <div><strong>Value:</strong> ${d.value} Wh/km</div>
                         </div>
                        `)
                    .style("left", event.pageX + 10 + "px")
                    .style("top", event.pageY - 10 + "px");
            })
            .on("mouseout", () =>
                tooltip.transition().duration(300).style("opacity", 0)
            );

        cells.append("text")
            .filter(d => d.value !== 0)
            .attr("x", d => xScale(d.x) + xScale.bandwidth() / 2)
            .attr("y", d => yScale(d.y) + yScale.bandwidth() / 2)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("font-size", Math.min(xScale.bandwidth(), yScale.bandwidth()) > 40 ? 14 : 10)
            .attr("fill", d => {
                const c = d3.rgb(colorScale(d.value));
                const luminance = 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
                return luminance < 140 ? "#ffffff" : "#111827";
            })
            .text(d => `${d.value} Wh/km`);

        g.append("g")
            .attr("transform", `translate(0, ${height})`)
            .call(d3.axisBottom(xScale).tickSize(0))
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick text").attr("fill", "#6b7280"));

        // X axis label
        g.append("text")
            .attr("x", width / 2)
            .attr("y", height + margin.bottom - 15) // adjust as needed
            .attr("text-anchor", "middle")
            .attr("fill", "#111827")
            .attr("font-size", 10)
            .text(xAxisName);

        // Y axis
        g.append("g")
            .call(d3.axisLeft(yScale).tickSize(0))
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick text").attr("fill", "#6b7280"));

        // Y axis label
        g.append("text")
            .attr("transform", "rotate(-90)")
            .attr("x", -height / 2)           // vertical center
            .attr("y", -margin.left + 20)     // move it further left outside tick labels
            .attr("text-anchor", "middle")
            .attr("fill", "#111827")
            .attr("font-size", 10)
            .text(yAxisName);

        return () => tooltip.remove();
    }, [data, xAxisName, yAxisName]);

    return (
        <div>
            <svg ref={ref}></svg>
        </div>
    );

}

export default Heatmap
